import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import StatusTimeline from '../../components/StatusTimeline';
import { startOnlinePayment, waitForPayment } from '../../services/payment';
import {
  Avatar, Badge, Button, Empty, Loading, Row,
} from '../../components/Ui';
import ChargeRows from '../../components/ChargeRows';
import { Reveal } from '../../components/Motion';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';
import {
  currency, dateTime, shortDate, titleCase,
} from '../../utils/format';

const FLOW = ['requested', 'accepted', 'on_the_way', 'in_progress', 'completed'];

const BookingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(() => {
    api.bookings.detail(id)
      .then((res) => setBooking(res.data))
      .catch(() => setBooking(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (pollRef.current && pollRef.current.cancel) pollRef.current.cancel(); }, []);

  // An active booking keeps refreshing so the timeline moves without a reload.
  useEffect(() => {
    if (!booking || ['completed', 'cancelled', 'rejected'].includes(booking.status)) return undefined;
    const timer = setInterval(load, 20000);
    return () => clearInterval(timer);
  }, [booking, load]);

  /** Reopens Razorpay for an unpaid online booking. */
  const payNow = async () => {
    setPaying(true);
    try {
      const { paymentId } = await startOnlinePayment({ refType: 'booking', refId: id });
      pollRef.current = waitForPayment(paymentId);
      const result = await pollRef.current;
      if (result.status === 'paid') {
        toast.success('Thank you! Your payment is confirmed.', 'Payment received');
        load();
      } else if (result.status !== 'timeout') {
        toast.error('The payment did not go through. You can try again.', 'Payment not completed');
      }
    } catch (err) {
      toast.error(err.message, 'Payment could not start');
    } finally {
      setPaying(false);
    }
  };

  /** Online payment failed or was abandoned: pay after the service instead. */
  const payLater = async () => {
    try {
      await api.bookings.setPaymentMode(id, { paymentMode: 'cod' });
      toast.success('You will pay the professional after the service.', 'Booking sent');
      load();
    } catch (err) { toast.error(err.message, 'Could not switch'); }
  };

  const cancel = async () => {
    const ok = await toast.confirm({
      title: 'Cancel booking',
      message: 'Are you sure you want to cancel this booking?',
      confirmLabel: 'Cancel booking',
      cancelLabel: 'Keep booking',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.bookings.cancel(id, { reason: 'Cancelled by customer' });
      load();
    } catch (err) {
      toast.error(err.message, 'Could not cancel');
    }
  };

  if (loading) return <Loading text="Loading booking…" />;
  if (!booking) {
    return <Empty emoji="📅" title="Booking not found" actionLabel="Go back" onAction={() => navigate(-1)} />;
  }

  const isClosed = ['cancelled', 'rejected'].includes(booking.status);
  const canCancel = !['completed', 'cancelled', 'rejected'].includes(booking.status);
  const showOtp = booking.otp && ['accepted', 'on_the_way'].includes(booking.status);

  return (
    <div>
      <PageHeader title={booking.bookingNo} subtitle={booking.serviceTitle} />

      <div className="split">
        <div>
          {/* The customer shares this when the professional arrives. */}
          {showOtp ? (
            <Reveal
              className="card sh-md"
              style={{
                background: colors.primary, border: 0, textAlign: 'center', marginBottom: 16,
              }}
            >
              <div style={{ color: 'rgba(255,255,255,.8)', fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4 }}>
                SHARE THIS OTP TO START THE SERVICE
              </div>
              <div style={{ color: '#fff', fontSize: 40, fontWeight: 900, letterSpacing: 10, marginTop: 6 }}>
                {booking.otp}
              </div>
            </Reveal>
          ) : null}

          <Reveal delay={60} className="card sh-sm">
            <div className="row-between">
              <Badge value={booking.status} />
              <span className="t-tiny">{dateTime(booking.createdAt)}</span>
            </div>

            {isClosed ? (
              <div
                className="row"
                style={{
                  gap: 12, marginTop: 14, background: colors.dangerBg,
                  padding: 14, borderRadius: 12, alignItems: 'flex-start',
                }}
              >
                <span style={{ fontSize: 22 }}>❌</span>
                <div className="grow">
                  <div className="t-title">Booking {booking.status}</div>
                  {booking.cancelReason ? <div className="t-small">{booking.cancelReason}</div> : null}
                </div>
              </div>
            ) : (
              <StatusTimeline flow={FLOW} status={booking.status} timeline={booking.timeline} />
            )}
          </Reveal>

          <Reveal delay={110} className="card sh-sm" style={{ marginTop: 16 }}>
            <h3 className="t-title" style={{ marginBottom: 8 }}>Service details</h3>
            <Row label="Service" value={booking.serviceTitle} />
            <Row
              label="Scheduled"
              value={`${shortDate(booking.scheduledDate)}${booking.scheduledSlot ? `\n${booking.scheduledSlot}` : ''}`}
            />
            <Row label="Quantity" value={String(booking.quantity)} />
            <ChargeRows doc={booking} />
            <Row label="Amount" value={currency(booking.totalAmount)} valueStyle={{ fontWeight: 800 }} />
            <Row
              label="Payment"
              value={`${(booking.paymentMode || '').toUpperCase()} · ${titleCase(booking.paymentStatus)}`}
            />
            {booking.requirement ? (
              <>
                <div className="divider" />
                <div className="t-small">Your requirement</div>
                <div className="t-body" style={{ marginTop: 4 }}>{booking.requirement}</div>
              </>
            ) : null}
          </Reveal>

          <Reveal delay={160} className="card sh-sm" style={{ marginTop: 16 }}>
            <h3 className="t-title" style={{ marginBottom: 12 }}>Service provider</h3>
            <div className="row" style={{ gap: 12 }}>
              <Avatar
                name={booking.partner && booking.partner.businessName}
                uri={fileUrl(booking.partner && booking.partner.logo)}
                size={48}
              />
              <div className="grow">
                <div className="t-title">{booking.partner && booking.partner.businessName}</div>
                <div className="t-small">📞 {booking.partner && booking.partner.phone}</div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={200} className="card sh-sm" style={{ marginTop: 16 }}>
            <h3 className="t-title" style={{ marginBottom: 8 }}>Service address</h3>
            <p className="t-body" style={{ lineHeight: 1.7 }}>
              {[
                booking.address && booking.address.line1,
                booking.address && booking.address.landmark,
                booking.address && booking.address.city,
                booking.address && booking.address.pincode,
              ].filter(Boolean).join(', ')}
            </p>
          </Reveal>
        </div>

        <div className="split-side col" style={{ gap: 12 }}>
          <Button
            title="Call provider"
            icon="📞"
            variant="ghost"
            full
            onClick={() => { window.location.href = `tel:${booking.partner && booking.partner.phone}`; }}
          />
          {booking.status === 'completed' ? (
            <Button
              title="Rate this service"
              icon="⭐"
              variant="accent"
              full
              onClick={() => navigate('/review', {
                state: {
                  targetType: 'service',
                  target: (booking.service && booking.service._id) || booking.service,
                  booking: booking._id,
                  title: booking.serviceTitle,
                },
              })}
            />
          ) : null}
          {booking.awaitingPayment && !isClosed ? (
            <div style={{ background: 'var(--warning-bg)', borderRadius: 12, padding: 12 }}>
              <strong style={{ color: 'var(--warning)' }}>⏳ Waiting for payment</strong>
              <div className="t-small" style={{ marginTop: 3 }}>
                {(booking.partner && booking.partner.businessName) || 'The professional'} gets this booking once it is paid.
              </div>
            </div>
          ) : null}
          {booking.awaitingPayment && !isClosed ? (
            <Button title="Pay after service instead" icon="💵" variant="light" full onClick={payLater} />
          ) : null}
          {booking.paymentMode === 'online' && booking.paymentStatus !== 'paid' && !isClosed ? (
            <Button
              title={`${booking.awaitingPayment ? 'Retry payment' : 'Pay'} · ${currency(booking.totalAmount)}`}
              icon="💳"
              variant="accent"
              loading={paying}
              full
              onClick={payNow}
            />
          ) : null}
          {canCancel ? <Button title="Cancel booking" variant="danger" full onClick={cancel} /> : null}
        </div>
      </div>
    </div>
  );
};

export default BookingDetailPage;
